import { useCallback, useEffect, useState } from "react";
import { countPendingMutations } from "../utils/journalOfflineStore";
import { flushJournalSyncQueue } from "../utils/journalSync";

export function useJournalOffline(tenantKey) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState(null);

  const refreshPending = useCallback(async () => {
    if (!tenantKey) {
      setPendingCount(0);
      return;
    }
    try {
      const n = await countPendingMutations(tenantKey);
      setPendingCount(n);
    } catch {
      setPendingCount(0);
    }
  }, [tenantKey]);

  const syncNow = useCallback(async () => {
    if (!tenantKey || isSyncing) return { synced: 0, failed: 0, remaining: 0 };
    setIsSyncing(true);
    setLastSyncError(null);
    try {
      const result = await flushJournalSyncQueue(tenantKey);
      await refreshPending();
      if (result.failed > 0) {
        setLastSyncError(`${result.failed} отметок не отправлено`);
      }
      return result;
    } catch (e) {
      setLastSyncError(e.message || "Ошибка синхронизации");
      return { synced: 0, failed: 0, remaining: pendingCount };
    } finally {
      setIsSyncing(false);
    }
  }, [tenantKey, isSyncing, pendingCount, refreshPending]);

  useEffect(() => {
    refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      syncNow();
    };
    const onOffline = () => setIsOnline(false);
    const onVisible = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        syncNow();
      }
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [syncNow]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncError,
    refreshPending,
    syncNow,
    isStale: !isOnline || pendingCount > 0,
  };
}
